import { Users, UserPlus, HeartHandshake, Headphones, Rocket, Infinity as InfinityIcon, ShieldCheck } from "lucide-react";

/* Universal value props — everything you get on EITHER base plan.
   Dark, dramatic block that pops against the page. */
const ITEMS = [
  {
    id: "practitioners",
    icon: UserPlus,
    value: "∞",
    valueLabel: "Unlimited",
    label: "Practitioners",
    detail: "Add every clinician — no per-seat fees, ever.",
    color: "text-blue-400",
    bar: "from-blue-500 to-blue-400",
    glow: "from-blue-500/30",
  },
  {
    id: "users",
    icon: Users,
    value: "∞",
    valueLabel: "Unlimited",
    label: "Team Users",
    detail: "Invite front desk, billing, marketing — all free.",
    color: "text-violet-400",
    bar: "from-violet-500 to-fuchsia-500",
    glow: "from-violet-500/30",
  },
  {
    id: "patients",
    icon: HeartHandshake,
    value: "∞",
    valueLabel: "Unlimited",
    label: "Patients",
    detail: "No patient caps. Bring your full chart history.",
    color: "text-emerald-400",
    bar: "from-emerald-500 to-teal-500",
    glow: "from-emerald-500/30",
  },
  {
    id: "support",
    icon: Headphones,
    value: "∞",
    valueLabel: "Unlimited",
    label: "Support",
    detail: "Live chat, email & phone — included on every plan, forever.",
    color: "text-amber-400",
    bar: "from-amber-500 to-orange-500",
    glow: "from-amber-500/30",
  },
  {
    id: "go-live",
    icon: Rocket,
    value: "14d",
    valueLabel: "Live in",
    label: "Go-Live",
    detail: "White-glove onboarding. Most clinics ship in 2 weeks.",
    color: "text-rose-400",
    bar: "from-rose-500 to-pink-500",
    glow: "from-rose-500/30",
  },
];

export const IncludedEverywhere = ({ id = "included-everywhere", className = "" }) => (
  <section
    id={id}
    data-testid="included-everywhere"
    className={`relative overflow-hidden ${className}`}
  >
    {/* Dark container with cinematic background */}
    <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
      <div className="relative overflow-hidden rounded-3xl border border-zinc-800 bg-zinc-950 px-6 py-10 sm:px-10 sm:py-14 lg:px-14 lg:py-16">
        {/* Decorative glows */}
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-violet-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-blue-500/15 blur-3xl" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />

        {/* Top accent line */}
        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-blue-500 via-violet-500 to-emerald-500" />

        <div className="relative">
          <p className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-300">
            <ShieldCheck className="h-3 w-3" />
            Included on every plan
          </p>
          <div className="mt-5 flex flex-col items-start gap-6 lg:flex-row lg:items-end lg:justify-between">
            <h2 className="font-display text-3xl font-black leading-[1.04] tracking-tight text-white sm:text-4xl lg:text-[3rem]">
              No per-seat games.{" "}
              <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-emerald-400 bg-clip-text text-transparent">
                Unlimited everything.
              </span>
            </h2>
            <p className="max-w-md text-[14px] leading-relaxed text-white/65 sm:text-[15px]">
              The same flat monthly price covers your whole team — no matter how many clinicians,
              users, patients or locations you run.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5 lg:gap-4">
            {ITEMS.map((item, i) => {
              const Icon = item.icon;
              const isWide = i === 4 && false; // reserved for future emphasis
              return (
                <div
                  key={item.id}
                  data-testid={`included-${item.id}`}
                  className={`group relative overflow-hidden rounded-2xl border border-zinc-800/80 bg-zinc-900/60 p-4 backdrop-blur transition-all hover:-translate-y-1 hover:border-zinc-700 hover:bg-zinc-900 sm:p-5 ${isWide ? "col-span-2" : ""}`}
                >
                  {/* tile corner glow */}
                  <div className={`pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br ${item.glow} to-transparent blur-2xl`} />

                  <div className="relative flex items-center justify-between">
                    <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-950 ring-1 ring-inset ring-white/10`}>
                      <Icon className={`h-[16px] w-[16px] ${item.color}`} />
                    </span>
                    {item.value === "∞" && (
                      <InfinityIcon className={`h-4 w-4 ${item.color}/70 opacity-50`} />
                    )}
                  </div>

                  <p className={`relative mt-4 text-[10.5px] font-bold uppercase tracking-[0.18em] ${item.color}`}>
                    {item.valueLabel}
                  </p>
                  <p className="relative font-display text-[2.5rem] font-black leading-[0.95] tracking-[-0.04em] text-white sm:text-[2.75rem]">
                    {item.value}
                  </p>

                  <p className="relative mt-3 font-display text-[14px] font-bold tracking-tight text-white">
                    {item.label}
                  </p>
                  <p className="relative mt-1 text-[12px] leading-snug text-white/55">
                    {item.detail}
                  </p>

                  {/* bottom accent bar */}
                  <div className={`absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r ${item.bar}`} />
                </div>
              );
            })}
          </div>

          <p className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11.5px] font-medium text-white/65 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            Same flat $297 or $497 — for the entire clinic. Forever.
          </p>
        </div>
      </div>
    </div>
  </section>
);
